import { useEffect, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { trpc } from "@/lib/trpc";
import { getLoginUrlForPlan } from "@/const";
import { usePaywallDialogOpen } from "@/hooks/usePaywallDialog";
import { closeGuestCheckout, useGuestCheckoutPlan, type BuyKey } from "@/hooks/useGuestCheckout";
import { trackCheckoutStart } from "@/lib/analytics";
import { currentPaywallToolId } from "@/lib/paywallEvents";

const LABEL: Record<BuyKey, string> = {
  day_pass: "Day Pass, $1",
  monthly: "Unlimited, $4.99 a month",
  premium_day_pass: "Premium Day Pass, $3",
  premium_monthly: "Premium Unlimited, $9.99 a month",
};
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Buy with just an email address: no account or password first. Payment is on Stripe's own page. */
export function GuestCheckoutDialog() {
  const plan = useGuestCheckoutPlan();
  const paywallOpen = usePaywallDialogOpen();
  // While a payment or checkout dialog is open, third-party consent/privacy pop-ups (Google's "Privacy & messaging"
  // message) are hidden by CSS, and our own cookie banner stays out (see ConsentBanner). They return on close.
  // This only hides: it never accepts or skips a choice, and script loading still waits for consent.
  const paymentOpen = plan !== null || paywallOpen;
  useEffect(() => {
    document.documentElement.classList.toggle("fdp-payment-open", paymentOpen);
    return () => document.documentElement.classList.remove("fdp-payment-open");
  }, [paymentOpen]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const pro = trpc.billing.createGuestCheckout.useMutation();
  const premium = trpc.premiumApps.guestCheckout.useMutation();

  // A monthly plan skips the email step: go straight to Stripe Checkout, which asks for the email itself. The account
  // is created or linked from that email when the payment completes, and a sign-in link is emailed.
  const direct = plan === "monthly" || plan === "premium_monthly";
  const started = useRef<BuyKey | null>(null);
  useEffect(() => {
    if (!plan) { started.current = null; return; }
    if (!direct || started.current === plan) return;
    started.current = plan;
    setBusy(true);
    const run = plan === "premium_monthly" ? premium.mutateAsync({ plan: "monthly" }) : pro.mutateAsync({ plan: "monthly" });
    run.then((r) => { trackCheckoutStart("monthly", plan === "premium_monthly" ? "premium" : "professional", currentPaywallToolId()); window.location.href = r.url; }).catch((err) => {
      toast.error(err instanceof Error && err.message ? err.message : "Couldn't start checkout. Please try again in a moment.");
      setBusy(false);
      closeGuestCheckout();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [plan, direct]);

  const go = async () => {
    if (!plan || busy || direct) return;
    if (!EMAIL_RE.test(email.trim())) { toast.error("Enter a valid email address."); return; }
    setBusy(true);
    try {
      const isPremium = plan.startsWith("premium_");
      const p = plan === "day_pass" || plan === "premium_day_pass" ? "day_pass" : "monthly";
      const r = isPremium ? await premium.mutateAsync({ plan: p, email: email.trim() }) : await pro.mutateAsync({ plan: p, email: email.trim() });
      trackCheckoutStart(p, isPremium ? "premium" : "professional", currentPaywallToolId());
      window.location.href = r.url;
    } catch (err) {
      toast.error(err instanceof Error && err.message ? err.message : "Couldn't start checkout. Please try again in a moment.");
      setBusy(false);
    }
  };

  return (
    <Dialog open={plan !== null} onOpenChange={(v) => { if (!v) { closeGuestCheckout(); setBusy(false); } }}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{plan ? LABEL[plan] : "Buy"}</DialogTitle>
          <DialogDescription>{direct ? "Taking you to our secure payment page. You enter your email there, and after paying we email you a link to sign in." : "Enter your email and go straight to payment. Your access is tied to this email, and you can sign in with it later using a link we send."}</DialogDescription>
        </DialogHeader>
        {direct ? (
          <div className="flex items-center justify-center gap-2 py-4 text-sm text-muted-foreground" role="status" data-testid="gc-redirecting">
            <Loader2 className="h-4 w-4 animate-spin" />Opening secure checkout
          </div>
        ) : (
        <form className="space-y-3" onSubmit={(e) => { e.preventDefault(); void go(); }}>
          <div className="space-y-1">
            <Label htmlFor="gc-email" className="text-xs">Email</Label>
            <Input id="gc-email" type="email" autoComplete="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@example.com" data-testid="gc-email" />
          </div>
          <Button type="submit" className="w-full gap-2" disabled={busy} data-testid="gc-go">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}Continue to payment
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Already have an account?{" "}
            <a className="underline" href={plan ? getLoginUrlForPlan(plan) : "#"}>Sign in first</a>
          </p>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
}
